import React, { useState, useEffect } from 'react';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import Add from '@material-ui/icons/Add';
import Business from '@material-ui/icons/Business';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/topupStyle';
import { useDispatch } from 'react-redux';
import CustomTable from 'components/CustomTable/CustomTable';
import TableHeader from 'components/TableHeader/TableHeader';
import { setToast } from 'redux/modules/toast/actions';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { AccountCell, DepartmentsCell, UserNameCell, PhoneNumberCell } from 'components/Cells/Cells';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import { BodyMBold, BodyM } from 'components/Typography/Typography';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import Actions from './subcomponents/Actions';
import TopUpCard from './subcomponents/TopUpCard';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CustomButton from 'components/CustomButtons/Button';
import EditCell from 'views/TopUp/subcomponents/EditCell';

// @ts-ignore
const useStyles = makeStyles(styles);

interface TopUpTableProps {
	selectedNumbers: types.topUp.INumber[];
	handleAddNumber: () => void;
	setSelectedNumbers: React.Dispatch<React.SetStateAction<types.topUp.INumber[]>>;
	accounts: { [key: string]: SingleCorpAccount };
	departments: { [key: string]: SingleDepartment };
	totalAmount: string | number;
	topupValue: number;
	setTopupValue: React.Dispatch<React.SetStateAction<number>>;
	handleTopUp: () => void;
}

const TopUpTable = ({
	selectedNumbers,
	handleAddNumber,
	topupValue,
	setTopupValue,
	setSelectedNumbers,
	totalAmount,
	accounts,
	departments,
	handleTopUp,
}: TopUpTableProps) => {
	const [customTopupValue, setCustomTopupValue] = useState<number>(0);
	const [searchValue, setSearchValue] = useState<string>('');
	const [selected, setSelected] = useState<Array<string>>([]);
	const [editedSelected, setEditedSelected] = useState<number[]>([]);
	const [editValue, setEditValue] = useState<number>(0);
	const [editValueEmpty, setEditValueEmpty] = useState<boolean>(false);
	const [isEmpty, setIsEmpty] = useState<boolean>(false);
	const dispatch = useDispatch();
	const classes = useStyles();

	const closeEdit = () => {
		setEditedSelected([]);
	};

	useEffect(() => {
		setSelected([]);
	}, [selectedNumbers, editedSelected]);

	const checkTopupValue = (value: string, setIsEmpty: Function, setValue: (value: number) => void) => {
		if (value === '') {
			setIsEmpty(true);
		} else if (/[0-9]+/.test(value)) {
			const numberValue = parseInt(value);
			if (numberValue > 0 && numberValue <= 100) {
				setValue(numberValue);
				setIsEmpty(false);
			}
		}
	};

	return (
		<>
			<div style={{ width: '100%' }} id="topup-container">
				<TopUpCard
					isEmpty={isEmpty}
					setIsEmpty={setIsEmpty}
					topupValue={topupValue}
					setTopupValue={setTopupValue}
					customTopupValue={customTopupValue}
					selectedNumbers={selectedNumbers}
					totalAmount={totalAmount}
					closeEdit={closeEdit}
					setCustomTopupValue={setCustomTopupValue}
					checkTopupValue={checkTopupValue}
				/>
				<TableHeader
					searchInputValue={searchValue}
					setSearchInputValue={(value) => setSearchValue(value)}
					buttons={{
						text: (
							<>
								<Add />
								{translate('payments.topup.addNumbersButton')}
							</>
						),
						onClick: () => handleAddNumber(),
						buttonProps: {
							id: 'add-numbers-button',
						},
					}}
				/>
				<div className={classes.tableWrapper}>
					<CustomTable
						withCheckbox
						withHeader
						withFooter
						total={selectedNumbers.length}
						tableProps={{
							data: selectedNumbers,
							defaultPageSize: 10,
							getTrProps: (_state, rowInfo: { index: number }) => {
								const props: { className?: string } = {};
								if (rowInfo) {
									if (selected.includes(String(rowInfo.index))) {
										props.className = classNames(props.className, classes.selectedRow);
									} else if (editedSelected.includes(rowInfo.index)) {
										props.className = classNames(props.className, classes.editedRow);
									}
								}

								return props;
							},
							columns: [
								{
									Header: translate('phoneNumber'),
									accessor: 'accountNum',
									Cell: ({ original }: types.topUp.TableRow) => (
										<PhoneNumberCell text={original.accountNum} />
									),
								},
								{
									Header: translate('payments.topup.userName'),
									accessor: 'login',
									Cell: ({ original }: types.topUp.TableRow) =>
										original.custom ? (
											<BodyM disableMargin>{translate('payments.topup.externalNumber')}</BodyM>
										) : (
											<UserNameCell text={original.login} type={original.userType} />
										),
								},
								{
									Header: translate('payments.topup.department'),
									accessor: 'deptId',
									Cell: (props: types.topUp.TableRow) => {
										const {
											original: { deptId },
										} = props;
										const dept = departments[deptId];
										return <DepartmentsCell departments={dept ? [dept] : []} />;
									},
								},
								{
									Header: translate('table.organization.name'),
									Cell: ({ original }: types.topUp.TableRow) => (
										<ConditionalRender
											show={!!accounts[original.accountId]}
											onTrue={() => (
												<AccountCell
													icon={
														<CustomAvatar
															accountId={original.accountId}
															placeholderIcon={<Business />}
															styles={{ width: 16, marginRight: 5 }}
															alt=""
														/>
													}
													text={accounts[original.accountId].accountName}
												/>
											)}
										/>
									),

									accessor: 'accountId',
								},
								{
									Header: translate('payments.topup.topupValue'),
									accessor: 'topUpValue',
									Cell: ({ original }: types.topUp.TableRow) => (
										<div>
											<BodyMBold inline>{String(original.topUpValue)}</BodyMBold>
											<BodyM inline> OMR</BodyM>
										</div>
									),
								},
								{
									Header: '',
									width: 50,
									className: classes.editCell,
									Cell: (row: types.topUp.TableRow) => {
										return (
											<Actions
												setTemporaryValue={(value) =>
													checkTopupValue(value, setEditValueEmpty, setEditValue)
												}
												resetTemporaryValue={() => setEditValue(row.original.topUpValue)}
												temporaryValue={editValueEmpty ? '' : String(editValue)}
												row={row}
												setValue={(value, index) => {
													checkTopupValue(value, setEditValueEmpty, (value) =>
														setSelectedNumbers((numbers) => {
															const newNumbers = [...numbers];
															newNumbers[index].topUpValue = value;
															return newNumbers;
														})
													);
													closeEdit();
												}}
												resetValue={(index) => {
													setSelectedNumbers((numbers) => {
														const newNumbers = [...numbers];
														newNumbers[index].topUpValue = topupValue;
														return newNumbers;
													});
													setEditedSelected([]);
												}}
												topupValue={topupValue}
												showEdit={() => {
													setEditValue(topupValue);
													setEditValueEmpty(false);
													setEditedSelected([row.index]);
												}}
												closeEdit={closeEdit}
												minEditedSelected={
													editedSelected.length === 1 ? editedSelected[0] : undefined
												}
											/>
										);
									},
								},
							],
						}}
						selected={selected}
						onSelect={setSelected}
						selectPanel={{
							confirmationDialogTitle: translate('topup.table.delete.elements'),
							confirmationDialogBody: translate('topup.table.are.you.sure'),
							selectedElements: selected.length,
							deleteItems: () => {
								dispatch(
									setToast({
										label: `${translate('payments.topup.deleted')}\n\n${selected.map(
											(index) => selectedNumbers[index].accountNum
										)}`,
										error: true,
									})
								);
								setSelectedNumbers((selectedNumbers: types.topUp.INumber[]) => {
									const newSelected = [...selectedNumbers];
									selected.reverse().forEach((index) => newSelected.splice(parseInt(index), 1));

									return newSelected;
								});
							},
							editItems: () => {
								const newSelected = selected.map(Number);
								setEditedSelected(newSelected);
								setEditValue(topupValue);
							},
						}}
					/>
					<ConditionalRender
						show={editedSelected.length > 1}
						onTrue={() => (
							<div className={classes.manyNumbersEdit}>
								<EditCell
									setTemporaryValue={(value) =>
										checkTopupValue(value, setEditValueEmpty, setEditValue)
									}
									resetTemporaryValue={() => setEditValue(topupValue)}
									temporaryValue={editValueEmpty ? '' : String(editValue)}
									setValue={(value) => {
										checkTopupValue(value, setEditValueEmpty, (value) =>
											setSelectedNumbers((numbers) => {
												const newNumbers = [...numbers];
												editedSelected.forEach((index) => {
													newNumbers[index].topUpValue = value;
												});

												return newNumbers;
											})
										);
										closeEdit();
									}}
									closeEdit={closeEdit}
								/>
							</div>
						)}
					/>
				</div>
				<div className={classes.tableFooter}>
					<GridContainer>
						<GridItem xs={6} md={4} lg={2} className={classes.topupButtonContainer}>
							<CustomButton color="primary-light" fullWidth onClick={handleTopUp}>
								{translate('payments.topup')}
							</CustomButton>
						</GridItem>
					</GridContainer>
				</div>
			</div>
		</>
	);
};

export default TopUpTable;
